/** @graphql agg **/
import { useCallback, useEffect, useState } from 'react';
import { fetchQuery, graphql, useFragment, type GraphQLTaggedNode } from 'react-relay';

import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useExternalLabelsQuery,
	useExternalLabelsQuery$data,
} from './__generated__/useExternalLabelsQuery.graphql.ts';
import type {
	useExternalLabels_query$data,
	useExternalLabels_query$key,
} from './__generated__/useExternalLabels_query.graphql.ts';

/**
 * Standalone Relay read of the managed (external) labels applied to a piece of
 * content.
 *
 * The View Page surfaces (`FooterLabels`, details-panel `LabelsSection`) read
 * managed labels via the colocated `useExternalLabels_query` fragment spread on
 * their owning query (see `useExternalLabelsFragment`), which dedups against the
 * `ContentUnifiedQuery`-warmed store. This standalone hook remains for the legacy
 * label picker (`useLabelsSelectProps`) only: its applied-labels read has an
 * `inheritedLabelsData` path typed as a `Content`-node fragment
 * (`ViewPageLabelsContentFragment_content`), which structurally cannot carry the
 * root-level `Query` field `confluence_externalLabels`, so a fragment-only read
 * there would have to reintroduce a separate query anyway. Migrating the picker
 * to fragment ownership is a documented follow-up.
 *
 * Uses idiomatic native Relay (`fetchQuery`) rather than the Apollo↔Relay interop
 * bridge (being ratcheted out). `fetchQuery` (not a suspending `useLazyLoadQuery`)
 * keeps the imperative, non-suspending `{ labels, loading, error, refetch }`
 * contract and lets the hook self-skip — the picker calls it unconditionally and
 * relies on the skip, and the optimistic apply/remove flow needs `refetch`.
 *
 * `store-or-network`: when the managed-labels gate is on, the `ContentUnifiedQuery`
 * spread has already warmed the same `confluence_externalLabels(cloudId, id)`
 * store record, so this reads it cache-first; otherwise it falls back to the
 * network on dialog open. No `X-Query-Context` header is sent — the field
 * resolves headerless (the prerequisite that let it fold into the batched
 * `ContentUnifiedQuery`).
 *
 * Gated behind `confluence_managed_external_labels_endpoint`.
 */
export const EXTERNAL_LABELS_QUERY: GraphQLTaggedNode = graphql`
	query useExternalLabelsQuery($cloudId: ID!, $id: ID!) {
		confluence_externalLabels(cloudId: $cloudId, id: $id) {
			id
			kind
			name
		}
	}
`;

/**
 * Colocated fragment on `Query` for the managed (external) labels field, so the
 * same selection can be spread into the View Page `ContentUnifiedQuery` (route
 * preload warms it into the Relay store) and into each consuming surface's own
 * query, then read via `useFragment`.
 *
 * `confluence_externalLabels(cloudId: ID!, id: ID!)` takes non-null args, so the
 * argument definitions are `ID!`. The spreading operation gates this behind
 * `@include(if: $includeExternalLabels)`; when excluded, the aliased fragment
 * ref is absent and the read returns an empty list. Mirrors the root-`Query`
 * fragment shape used by `preloadAutohighlightsQuery_query`.
 */
export const EXTERNAL_LABELS_FRAGMENT: GraphQLTaggedNode = graphql`
	fragment useExternalLabels_query on Query
	@argumentDefinitions(cloudId: { type: "ID!" }, id: { type: "ID!" }) {
		confluence_externalLabels(cloudId: $cloudId, id: $id) {
			id
			kind
			name
		}
	}
`;

export type ExternalLabel = NonNullable<
	NonNullable<useExternalLabelsQuery$data['confluence_externalLabels']>[number]
>;

export type UseExternalLabelsResult = {
	labels: readonly ExternalLabel[];
	loading: boolean;
	error: Error | undefined;
	refetch: () => void;
};

// State is kept in a single object so each transition is one atomic update
// rather than a chain of `setLabels`/`setLoading`/`setError` calls.
type ExternalLabelsState = {
	labels: readonly ExternalLabel[];
	loading: boolean;
	error: Error | undefined;
};

const SETTLED_EMPTY: ExternalLabelsState = { labels: [], loading: false, error: undefined };

/**
 * Builds the page ARI used to identify the content when reading applied labels.
 * Exported so the route preloader and each fragment-owning consumer query build
 * the exact same `confluence_externalLabels` arguments (cloudId + ARI).
 */
export const buildExternalLabelsAri = (cloudId: string, contentId: string): string =>
	cloudId && contentId ? `ari:cloud:confluence:${cloudId}:page/${contentId}` : '';

export const useExternalLabels = (contentId: string): UseExternalLabelsResult => {
	const { cloudId } = useSessionData();
	const entityAri = buildExternalLabelsAri(cloudId, contentId);

	const skip = !fg('confluence_managed_external_labels_endpoint') || !cloudId || !contentId;

	// Start in the loading state whenever the query is active, so consumers don't
	// flash an empty state before the first read resolves.
	const [state, setState] = useState<ExternalLabelsState>(() =>
		skip ? SETTLED_EMPTY : { labels: [], loading: true, error: undefined },
	);

	const runQuery = useCallback(() => {
		if (skip) {
			// Not active: present a clean, empty, settled state and fire no request.
			setState(SETTLED_EMPTY);
			return () => {};
		}

		setState((prev) => ({ ...prev, loading: true, error: undefined }));

		let cancelled = false;

		const subscription = fetchQuery<useExternalLabelsQuery>(
			getRelayEnvironment(),
			EXTERNAL_LABELS_QUERY,
			{ cloudId, id: entityAri },
			{
				// Read from the Relay store first (warmed by `preloadManagedLabels`
				// during SSR) and fall back to the network otherwise.
				fetchPolicy: 'store-or-network',
				// HEADERLESS TEST (uncommitted): the `X-Query-Context` header was
				// removed to verify whether `confluence_externalLabels` resolves
				// without it. This is the prerequisite for folding the field into
				// ContentUnifiedQuery (a per-request header would force that batched
				// View Page query out of the batch). Restore the header if the
				// gateway rejects the request.
			},
		).subscribe({
			next: (data) => {
				if (cancelled) {
					return;
				}
				// The gateway types both the array and its elements as nullable; drop any
				// null entries so callers get a clean `ExternalLabel[]`.
				setState({
					labels: (data?.confluence_externalLabels ?? []).filter(
						(label): label is ExternalLabel => label != null,
					),
					loading: false,
					error: undefined,
				});
			},
			error: (err: unknown) => {
				if (cancelled) {
					return;
				}
				setState((prev) => ({
					...prev,
					loading: false,
					error: err instanceof Error ? err : new Error(String(err)),
				}));
			},
		});

		return () => {
			cancelled = true;
			subscription.unsubscribe();
		};
	}, [skip, cloudId, entityAri]);

	// Returns `runQuery`'s cleanup so the in-flight fetch subscription is torn
	// down on unmount / re-run — this is a subscription effect, not a chain of
	// derived-state updates.
	useEffect(() => {
		return runQuery();
	}, [runQuery]);

	return {
		labels: state.labels,
		loading: state.loading,
		error: state.error,
		refetch: () => {
			if (!skip) {
				runQuery();
			}
		},
	};
};

/**
 * Reads managed (external) labels from a `useExternalLabels_query` fragment ref
 * that a parent operation exposes (e.g. the aliased spread on
 * `ContentUnifiedQuery` or a surface's own query). This is the fragment-ownership
 * path: the owning query fetches the data once and each surface reads it via
 * `useFragment` with no separate request.
 *
 * The gate lives on the spreading operation (`@include(if: $includeExternalLabels)`),
 * so an absent/omitted ref simply yields an empty list here — callers still gate
 * the managed-label UI inline. Unlike the standalone `useExternalLabels`, this
 * hook is a pure store read: loading/error are owned by the parent query.
 */
export const useExternalLabelsFragment = (
	fragmentRef: useExternalLabels_query$key | null | undefined,
): readonly ExternalLabel[] => {
	const data: useExternalLabels_query$data | null | undefined = useFragment(
		EXTERNAL_LABELS_FRAGMENT,
		fragmentRef ?? null,
	);

	// The gateway types both the array and its elements as nullable; drop any
	// null entries so callers get a clean `ExternalLabel[]`.
	return (data?.confluence_externalLabels ?? []).filter(
		(label): label is ExternalLabel => label != null,
	);
};

export type ExternalLabelsFragmentVariables = {
	includeExternalLabels: boolean;
	cloudId: string;
	id: string;
};

/**
 * Builds the three `useExternalLabels_query` spread variables for a consumer
 * query, deriving `includeExternalLabels` identically everywhere from the
 * managed-labels feature gate (`confluence_managed_external_labels_endpoint`).
 *
 * This is a net-new, fully-gated feature — there is no pre-existing read path to
 * migrate — so it is NOT coupled to the View Page document-contract experiment
 * (`confluence_exp_vp_document`); that experiment exists to cut existing queries
 * over from an old path to the unified-query path, which does not apply here. The
 * route preloader derives the `ContentUnifiedQuery` warm from the same gate, so
 * SSR warm and each client read stay on one logical operation.
 *
 * `cloudId`/`id` are always returned (non-null, no GraphQL default); they are
 * empty strings when excluded, in which case the `@include`-gated spread is
 * skipped and the server never reads them.
 */
export const useExternalLabelsFragmentVariables = (
	contentId: string,
): ExternalLabelsFragmentVariables => {
	const { cloudId } = useSessionData();
	const includeExternalLabels =
		Boolean(cloudId) && Boolean(contentId) && fg('confluence_managed_external_labels_endpoint');

	return {
		includeExternalLabels,
		cloudId: includeExternalLabels ? cloudId : '',
		id: includeExternalLabels ? buildExternalLabelsAri(cloudId, contentId) : '',
	};
};
